/**
 * AmbassadorSlotsPanel — admin-only management UI for the 5 reserved
 * ambassador slots (001-005). 001 is the main admin; 002-005 are
 * co-admin spots. Lets the admin assign / vacate occupants without
 * leaving the dashboard.
 *
 * Hits:
 *   GET  /api/admin/ambassador/slots
 *   POST /api/admin/ambassador/slots/{number}/assign  (body: {email})
 *   POST /api/admin/ambassador/slots/{number}/release
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Megaphone, UserPlus, X, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const AmbassadorSlotsPanel = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [assignFor, setAssignFor] = useState(null); // slot number
  const [emailInput, setEmailInput] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/ambassador/slots`);
      if (!r.ok) return;
      const d = await r.json();
      setSlots(d.slots || []);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitAssign = async (number) => {
    if (!emailInput.trim()) { toast.error('Enter an email'); return; }
    setBusy(number);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/ambassador/slots/${number}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Assign failed'); return; }
      toast.success(`Slot #${number} assigned to ${emailInput}`);
      setAssignFor(null); setEmailInput('');
      refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setBusy('');
    }
  };

  const release = async (number) => {
    if (!window.confirm(`Vacate slot #${number}? The King will be demoted back to their previous role.`)) return;
    setBusy(number);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/ambassador/slots/${number}/release`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Release failed'); return; }
      toast.success(`Slot #${number} vacated`);
      refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="rounded-2xl border border-pink-500/30 bg-slate-950/40 p-5 space-y-4" data-testid="ambassador-slots-panel">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-pink-300" />
          <h2 className="text-lg font-bold text-white">King &amp; Ambassador slots</h2>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="text-xs text-pink-200/80 hover:text-pink-100"
          data-testid="ambassador-slots-refresh"
        >Refresh</button>
      </header>
      <p className="text-xs text-slate-400">
        001 is the main admin (you). 002-031 are reserved <strong className="text-amber-200">King</strong> spots (30 total)
        — assigning a user automatically promotes them to <code className="text-amber-200">king</code> and links them to <code className="text-pink-200">#001</code> as their recruiter.
        Kings can then appoint Ambassadors from their throne-room dashboard.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading slots…</div>
      ) : (
        <div className="space-y-2">
          {slots.map((s) => (
            <div
              key={s.number}
              className={`rounded-xl border ${s.vacant ? 'border-slate-700 bg-slate-900/40' : 'border-pink-500/40 bg-pink-500/10'} p-3 flex items-center gap-3`}
              data-testid={`ambassador-slot-row-${s.number}`}
            >
              <div className="w-12 h-12 rounded-lg bg-slate-900 grid place-items-center shrink-0">
                <span className="text-pink-200 font-bold tabular-nums">#{s.number}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white">{s.label}</p>
                {s.occupant ? (
                  <p className="text-xs text-slate-300 truncate" data-testid={`ambassador-slot-occupant-${s.number}`}>
                    <span className="text-pink-200">{s.occupant.name || s.occupant.email}</span>
                    <span className="text-slate-500"> · {s.occupant.role}</span>
                  </p>
                ) : (
                  <p className="text-xs text-amber-300/90">
                    Vacant{s.name_hint ? ` — reserved for ${s.name_hint}` : ''}
                  </p>
                )}
              </div>
              {s.vacant ? (
                assignFor === s.number ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="user@email"
                      className="rounded-md bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 w-44 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                      data-testid={`ambassador-slot-email-${s.number}`}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => submitAssign(s.number)}
                      disabled={busy === s.number}
                      className="text-xs font-bold rounded-md bg-pink-500 hover:bg-pink-400 text-white px-2.5 py-1.5 disabled:opacity-50"
                      data-testid={`ambassador-slot-assign-confirm-${s.number}`}
                    >
                      {busy === s.number ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Assign'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAssignFor(null); setEmailInput(''); }}
                      className="text-xs rounded-md bg-slate-800 text-slate-300 px-2 py-1.5 hover:bg-slate-700"
                      data-testid={`ambassador-slot-assign-cancel-${s.number}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  s.number !== '001' && (
                    <button
                      type="button"
                      onClick={() => { setAssignFor(s.number); setEmailInput(''); }}
                      className="inline-flex items-center gap-1 text-xs font-bold rounded-md bg-pink-500/30 hover:bg-pink-500/50 text-pink-100 px-2.5 py-1.5"
                      data-testid={`ambassador-slot-assign-${s.number}`}
                    >
                      <UserPlus className="w-3 h-3" /> Assign
                    </button>
                  )
                )
              ) : (
                s.number !== '001' && (
                  <button
                    type="button"
                    onClick={() => release(s.number)}
                    disabled={busy === s.number}
                    className="text-xs rounded-md bg-rose-500/30 hover:bg-rose-500/50 text-rose-100 px-2.5 py-1.5 disabled:opacity-50"
                    data-testid={`ambassador-slot-release-${s.number}`}
                  >
                    {busy === s.number ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Vacate'}
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AmbassadorSlotsPanel;
